import * as Sentry from "@sentry/react";
import { Switch, Route, useLocation } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";

// Build hash — changes on every code update
const BUILD_HASH = "t4q829";
console.log(`%c[Kintsugi] build ${BUILD_HASH}`, "color: #7c3aed; font-weight: bold");
import { QueryClientProvider } from "@tanstack/react-query";
import { PostHogProvider } from "posthog-js/react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ClientViewProvider } from "@/context/ClientViewContext";
import { SidebarProvider } from "@/context/SidebarContext";
import { EstimateRefreshProvider } from "@/context/EstimateRefreshContext";
import { EstimateRefreshWatcher } from "@/components/EstimateRefreshWatcher";
import { OfflineBanner } from "@/components/OfflineBanner";
import { InstallPromptBanner } from "@/components/InstallPromptBanner";
import { UpdateToast } from "@/components/UpdateToast";
import { AppErrorFallback } from "@/components/AppErrorFallback";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIdentify } from "@/hooks/useIdentify";
import { POSTHOG_KEY, posthogOptions } from "@/lib/posthog";
import type { SessionUser } from "@/lib/auth";

const MobileApp = lazy(() => import("@/mobile/MobileApp").then((m) => ({ default: m.MobileApp })));
const NotFound = lazy(() => import("@/pages/not-found"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ClientsList = lazy(() => import("@/pages/ClientsList"));
const Users = lazy(() => import("@/pages/Users"));
const Login = lazy(() => import("@/pages/Login"));
const SetupAccount = lazy(() => import("@/pages/SetupAccount"));
const Bootstrap = lazy(() => import("@/pages/Bootstrap"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Imports = lazy(() => import("@/pages/Imports"));
const Settings = lazy(() => import("@/pages/Settings"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const ApiManagement = lazy(() => import("@/pages/ApiManagement"));
const Profile = lazy(() => import("@/pages/Profile"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="text-muted-foreground">Loading…</p>
    </div>
  );
}

function AuthGuard({ children, adminOnly }: { children: React.ReactNode; adminOnly?: boolean }) {
  const [, navigate] = useLocation();
  const { data: user, isLoading } = useQuery<SessionUser | null>({
    queryKey: ["/api/auth/me"],
    queryFn: () => fetch("/api/auth/me", { credentials: "include" }).then((r) => {
      if (!r.ok) return null;
      return r.json();
    }),
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [isLoading, user, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!user) return null;

  const isAdmin = user.role === "admin" || user.realUser?.role === "admin";
  if (adminOnly && !isAdmin) return <NotFound />;

  return <>{children}</>;
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/setup-account" component={SetupAccount} />
      <Route path="/bootstrap" component={Bootstrap} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/properties/detail/:propertyId">
        <AuthGuard>
          <Dashboard />
        </AuthGuard>
      </Route>
      <Route path="/">
        <AuthGuard>
          <Dashboard />
        </AuthGuard>
      </Route>
      <Route path="/clients">
        <AuthGuard adminOnly>
          <ClientsList />
        </AuthGuard>
      </Route>
<Route path="/users">
        <AuthGuard adminOnly>
          <Users />
        </AuthGuard>
      </Route>
      <Route path="/imports">
        <AuthGuard adminOnly>
          <Imports />
        </AuthGuard>
      </Route>
      <Route path="/settings">
        <AuthGuard adminOnly>
          <Settings />
        </AuthGuard>
      </Route>
      <Route path="/retry-queue">
        <AuthGuard adminOnly>
          <ApiManagement />
        </AuthGuard>
      </Route>
      <Route path="/feedback">
        <AuthGuard adminOnly>
          <Feedback />
        </AuthGuard>
      </Route>
      <Route path="/api-management">
        <AuthGuard adminOnly>
          <ApiManagement />
        </AuthGuard>
      </Route>
      <Route path="/profile">
        <AuthGuard>
          <Profile />
        </AuthGuard>
      </Route>
      <Route component={NotFound} />
    </Switch>
    </Suspense>
  );
}

function AppShell() {
  useIdentify();
  const isMobile = useIsMobile();

  if (isMobile === undefined) {
    // Avoid flash of wrong layout while hook resolves
    return null;
  }

  if (isMobile) {
    return (
      <Suspense fallback={<PageLoader />}>
        <ClientViewProvider>
          <MobileApp />
        </ClientViewProvider>
      </Suspense>
    );
  }

  return (
    <SidebarProvider>
      <ClientViewProvider>
        <EstimateRefreshProvider>
          <EstimateRefreshWatcher />
          <Router />
        </EstimateRefreshProvider>
      </ClientViewProvider>
    </SidebarProvider>
  );
}

function App() {
  const tree = (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
          <UpdateToast />
          <OfflineBanner />
          <InstallPromptBanner />
          <Toaster />
          <AppShell />
        </Sentry.ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );

  // PostHogProvider with an undefined apiKey would crash, so conditionally
  // wrap only when the key is present (graceful degradation per ANLY-03).
  if (POSTHOG_KEY) {
    return (
      <PostHogProvider apiKey={POSTHOG_KEY} options={posthogOptions}>
        {tree}
      </PostHogProvider>
    );
  }

  return tree;
}

export default App;
